import * as React from 'react';
import {useState} from "react";
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import {Box, Checkbox, Chip, IconButton, Tooltip} from '@mui/material';
import PropTypes from "prop-types";
import {useNavigate} from "react-router";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import PublicIcon from '@mui/icons-material/Public';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CreateProductDialog from "./CreateProductDialog";
import DeleteProductDialog from "../Shop/DeleteProductDialog";
import ResponseDialog from "../ResponseDialog";
import {useSetHideForProductMutation} from "../../core/api/products-api";

const getStockInfo = (product) => {
    const parts = [];
    let total = 0;
    let hasWarning = false;

    if (!product.variations || product.variations.length === 0) {
        if (!product.storageAmounts || product.storageAmounts.length === 0) {
            return {hasWarning: true, total: 0, fullText: "Product is out of stock"};
        }
        const empty = [];
        const inStock = [];
        product.storageAmounts.forEach(item => {
            if (item.amount === 0) {
                empty.push(item.location);
            } else {
                inStock.push(item.location + ": " + item.amount);
                total += item.amount;
            }
        })
        if (empty.length !== 0) {
            hasWarning = true;
            parts.push("Out of stock - " + empty.join(", "));
        }
        if (inStock.length !== 0) {
            parts.push("In stock - " + inStock.join(" pcs, ") + " pcs");
        }
    } else {
        if (!product.storageAmounts || product.storageAmounts.length === 0) {
            return {hasWarning: true, total: 0, fullText: "All sizes are out of stock"};
        }
        const emptyInLocation = [];
        const inStockInLocation = [];
        product.storageAmounts.forEach(item => {
            const empty = [];
            const inStock = [];
            const variationsMap = item.variationsAmountMap instanceof Map ?
                item.variationsAmountMap :
                new Map(Object.entries(item.variationsAmountMap || {}));
            if (variationsMap.size !== 0) {
                variationsMap.forEach((value, key) => {
                    if (value === 0) {
                        empty.push(key);
                    } else {
                        inStock.push(key + "-" + value + "pcs");
                        total += value;
                    }
                })
            }
            if (empty.length !== 0) {
                emptyInLocation.push(item.location + ": " + empty.join(", "));
            }
            if (inStock.length !== 0) {
                inStockInLocation.push(item.location + ": " + inStock.join(", "));
            }
        })
        if (emptyInLocation.length !== 0) {
            hasWarning = true;
            parts.push("Out of stock - " + emptyInLocation.join(", "));
        }
        if (inStockInLocation.length !== 0) {
            parts.push("In stock - " + inStockInLocation.join(", "));
        }
    }
    return {hasWarning, total, fullText: parts.join(". ")};
}

export default function AdminProductCard(props) {
    const [state, setState] = useState({
        isResponseDialogOpen: false,
        responseData: {
            isUpdate: false,
            responseData: {}
        },
        isEditProductDialog: false,
        isCopyProductDialog: false,
        isDeleteProductDialogOpen: false,
    });
    const [setHideForProduct] = useSetHideForProductMutation();
    const navigate = useNavigate();

    const stock = getStockInfo(props.product);
    const stockLabel = !stock.hasWarning ? `In stock · ${stock.total}` :
        stock.total > 0 ? "Partly in stock" : "Out of stock";
    const stockColor = !stock.hasWarning ? "success" :
        stock.total > 0 ? "warning" : "error";

    //TODO rework into a link component
    const handleCardClick = () => {
        navigate('/products/' + props.product.id, {state: props.product})
    }
    const handleHideToggle = () => {
        setHideForProduct({
            objectId: props.product.id,
            isHidden: !props.product.isHidden
        })
    }

    const handleDeleteProduct = () => {
        setState({
            ...state,
            isDeleteProductDialogOpen: true
        })
    }
    const handleEditButton = () => {
        setState({
            ...state,
            isEditProductDialog: true,
            isCopyProductDialog: false,
        })
    }
    const handleCopyButton = () => {
        setState({
            ...state,
            isEditProductDialog: true,
            isCopyProductDialog: true,
        })
    }

    const handleClose = (responseData) => {
        setState({
            ...state,
            isResponseDialogOpen: responseData.isResponseReady,
            responseData: responseData.data,
            isEditProductDialog: false,
            isDeleteProductDialogOpen: false
        })
    };

    const handleResponseDialogClose = () => {
        setState({
            ...state,
            isResponseDialogOpen: false
        })
    }


    return (
        <Box className={"product-card admin-product-card"}>
            <Card sx={{display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 1}}>
                <Tooltip title="Drag to reorder" placement="top">
                    <Box {...(props.dragHandleProps || {})}
                         sx={{display: 'flex', color: 'text.disabled', cursor: 'grab'}}>
                        <DragIndicatorIcon/>
                    </Box>
                </Tooltip>
                <Checkbox
                    color="warning"
                    checked={props.isSelected}
                    onChange={() => props.onToggleSelect(props.product.id)}
                    inputProps={{'aria-label': `Select ${props.product.title}`}}
                />
                <CardMedia
                    component="img"
                    src={props.product.images[0]}
                    image={props.product.images[0]}
                    alt={props.product.title}
                    onClick={handleCardClick}
                />
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography
                        onClick={handleCardClick}
                        sx={{fontWeight: 600, cursor: 'pointer', mb: 0.5}}
                        component="div">
                        {props.product.title}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
                        <Tooltip placement={"top"}
                                 title={<Typography fontSize={14}>Available in locations</Typography>}>
                            <Chip
                                icon={<PublicIcon/>}
                                label={props.product.availability ? props.product.availability.join(', ') : ""}
                                variant={"outlined"}
                                size={"small"}
                                color="secondary"/>
                        </Tooltip>
                        <Tooltip placement={"top"}
                                 title={<Typography fontSize={14}>{stock.fullText}</Typography>}>
                            <Chip
                                icon={stock.hasWarning ?
                                    <ReportProblemOutlinedIcon/> :
                                    <CheckCircleOutlineIcon/>}
                                label={stockLabel}
                                variant={"outlined"}
                                size={"small"}
                                color={stockColor}/>
                        </Tooltip>
                        {props.product.hasReadyComment &&
                            <Chip
                                icon={<CommentOutlinedIcon/>}
                                label="Auto-comment"
                                variant={"outlined"}
                                size={"small"}
                                color="warning"/>
                        }
                        {props.product.isHidden &&
                            <Chip
                                icon={<VisibilityOffIcon/>}
                                label="Hidden"
                                variant={"outlined"}
                                size={"small"}/>
                        }
                        {props.product.isSeniority &&
                            <Chip
                                label="Seniority"
                                variant={"outlined"}
                                size={"small"}/>
                        }
                    </Box>
                </Box>
                <Box sx={{display: 'flex', flex: 'none'}}>
                    <Tooltip title={props.product.isHidden ? "Show" : "Hide"}>
                        <IconButton onClick={handleHideToggle}>
                            {props.product.isHidden ? <VisibilityIcon/> : <VisibilityOffIcon/>}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton onClick={handleEditButton}>
                            <EditIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy">
                        <IconButton onClick={handleCopyButton}>
                            <ContentCopyIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={handleDeleteProduct}>
                            <DeleteOutlineIcon/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Card>
            <CreateProductDialog
                open={state.isEditProductDialog}
                onClose={handleClose}
                isEditMode={true}
                isCopyMode={state.isCopyProductDialog}
                product={props.product}
                refetchProduct={props.refetchProduct}
            />
            <DeleteProductDialog onClose={handleClose}
                                 open={state.isDeleteProductDialogOpen}
                                 productId={props.product.id}
                                 productName={props.product.title}
                                 refetchProduct={props.refetchProduct}
            />
            {state.responseData &&
                <ResponseDialog
                    onClose={handleResponseDialogClose}
                    open={state.isResponseDialogOpen}
                    responseData={state.responseData.responseData ? state.responseData.responseData : {}}
                    label={state.responseData.isUpdate ? "Product update" : "Product creation"}
                    refetch={props.refetchProduct}
                />
            }
        </Box>

    );
}
AdminProductCard.propTypes = {
    product: PropTypes.object.isRequired,
    refetchProduct: PropTypes.func.isRequired,
    dragHandleProps: PropTypes.object,
    isSelected: PropTypes.bool.isRequired,
    onToggleSelect: PropTypes.func.isRequired,
};
